<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Comments, {type GetMethodType, type CommentUpdateBody} from '@/components/Comments.vue';
import { useTypedStore } from '@/store';
import { useFormatters } from '@/composables/useFormatters';
import UiChevron from '@/views/CRM/components/UiChevron.vue';
import PaymentDuplicates from '@/views/MediaBuying/components/PaymentDuplicates.vue';
import { useCopy } from '@/composables/useCopy';
import { typeGuard } from '@/types/core';
import CampaignStatisticFilterPopup from './CampaignStatisticFilterPopup.vue';
import type { GetMediaBuyingPaymentsByIdResponse, GetMediaBuyingPostsByIdResponse } from '@/types/openapi-ts';
import type { Item } from '@/components/Breadcrumbs.vue';
//#endregion

//#region types
type MediaBuyingPaymentsPermissions = NonNullable<GetMediaBuyingPaymentsByIdResponse['misc']>['permissions'];
type MediaBuyingPostsPermission = NonNullable<GetMediaBuyingPostsByIdResponse['misc']>['permissions'];

interface BaseProps {
  breadcrumbs: Item[];
  currencyCode: string;
  isLoading?: boolean;
  period?: string[] | null;
  getComments: GetMethodType;
  postComment: (comment: CommentUpdateBody) => void;
}

interface PostProps extends BaseProps {
  type: 'post';
  info: GetMediaBuyingPostsByIdResponse['payload'];
  permissions: MediaBuyingPostsPermission;
}

interface PaymentProps extends BaseProps {
  type: 'payment';
  info: GetMediaBuyingPaymentsByIdResponse['payload'];
  permissions: MediaBuyingPaymentsPermissions;
}

type Props = PostProps | PaymentProps;

interface Emits {
  'apply-filters': [value: string[]]
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  period: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useTypedStore();
const route = useRoute();
const { t } = useI18n();
const { formatCurrencyValue, formatMoneyNoFrac } = useFormatters();
const { copy } = useCopy();
//#endregion

//#region state
const expandedAffiliateBalance = ref(false);
const expandedCampaignBalance = ref(false);
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const postPlatform = computed(() => store.getters['misc/postPlatform']);

const affiliateInfo = computed(() => createInfoArray(balanceFieldsAffiliateConverted.value));
const campaignInfo = computed(() => createInfoArray(balanceFieldsCampaignConverted.value));

const affiliateStats = computed(() => createStatsArray(props.info.affiliate?.stats));
const campaignStats = computed(() => createStatsArray(props.info.campaign?.stats));

const methodImgUrl = computed(() => {
  if(props.type === 'payment') {
    return props.info.payment_method_logo_base64 !== null
      ? props.info.payment_method_logo_base64
      : false;
  }
  return false;
});

const titleText = computed(() => {
  const translationKey = props.type === 'post'
    ? 'mediaBuying.info.post_title'
    : 'mediaBuying.info.title';

  return t(translationKey, { affiliate: props.info?.affiliate_email });
});

const previewImagesSrcList = computed(() => {
  return (
    props.info.files
      .filter((img) => img.preview_url !== null)
      .map((img) => img.preview_url!) || []
  );
});

const balanceFieldsAffiliateConverted = computed(() => {
  return props.info.affiliate?.money_converted || {};
});
const balanceFieldsCampaignConverted = computed(() => {
  return props.info?.campaign?.money_converted || {};
});

const isRootCampaign = computed(() => {
  return props.info.root_campaign_incrementing_id === props.info.campaign_incrementing_id;
});

const hasLinkToReports = computed(() => {
  if (!route.fullPath.startsWith('/media-buying/payments')) {
    return false;
  }

  const { approved = 0 } = props.info?.campaign?.money_usd?.balance || {};
  const hasStats = Object.values(props.info?.campaign?.stats || {}).some((stat) => stat !== 0);

  return approved > 0 || hasStats;
});

const urlFilter = computed(() => {
  if (props.info.urls) {
    return props.info.urls.filter((el) => el.url.trim() !== '');
  }
  return [];
});
//#endregion

//#region methods
function hasPermission(kind: keyof typeof props.permissions) {
  return props.permissions[kind] || adminAcl.value.is_superuser;
}

function handleAffiliateBalance() {
  expandedAffiliateBalance.value = !expandedAffiliateBalance.value;
}

function handleCampaignBalance() {
  expandedCampaignBalance.value = !expandedCampaignBalance.value;
}

function applyCampaignStatistics(period: string[]) {
  emit('apply-filters', period);
}

function typeBalance(num: number) {
  return `media-buying-info__item-value_${num >= 0 ? 'positive' : 'negative'}`;
}

function createInfoArray(source: any) {
  return [
    { name: t('mediaBuying.other.paid_in_total'), value: formatCurrencyValue(props.currencyCode, source.payments?.approved), valueClass: 'media-buying-info__item-value' },
    { name: t('mediaBuying.other.paid_pending'), value: formatCurrencyValue(props.currencyCode, source.payments?.pending), valueClass: 'media-buying-info__item-value' },
    { name: t('mediaBuying.other.spent_total'), value: formatCurrencyValue(props.currencyCode, source.posts?.approved), valueClass: 'media-buying-info__item-value media-buying-info__item-value_negative' },
    { name: t('mediaBuying.other.spent_pending'), value: formatCurrencyValue(props.currencyCode, source.posts?.pending), valueClass: 'media-buying-info__item-value media-buying-info__item-value_negative' },
  ];
}

function createStatsArray(stats: any) {
  const totalProfit = stats?.total_profit_sum_usd!;
  return [
    { name: t('mediaBuying.statistics.registrations_count'), value: formatMoneyNoFrac(stats?.registrations_count) },
    { name: t('mediaBuying.statistics.first_deposits_count'), value: formatMoneyNoFrac(stats?.first_deposits_count) },
    { name: t('mediaBuying.statistics.deposits_sum_usd'), value: formatCurrencyValue(props.currencyCode, stats?.deposits_sum_usd) },
    { name: t('mediaBuying.statistics.profit_sum_usd'), value: formatCurrencyValue(props.currencyCode, stats?.profit_sum_usd) },
    { name: t('mediaBuying.statistics.total_profit_sum_usd'), value: formatCurrencyValue(props.currencyCode, totalProfit), valueClass: typeBalance(totalProfit) },
    { name: t('mediaBuying.statistics.cost_per_player_usd'), value: formatCurrencyValue(props.currencyCode, stats?.cost_per_player_usd!) },
  ];
}

function getIcon(url: typeof props.info.urls[number]) {
  const id = url.url_platform_id;
  return (postPlatform.value.find((el) => el.id === id))?.logo_base64;
}
//#endregion
</script>

<template>
  <div
    v-loading="isLoading"
    class="media-buying-info"
  >
    <div class="t:space-y-16">
      <Breadcrumbs :items="props.breadcrumbs" />
      <div class="t:flex t:items-center t:gap-8">
        <div class="media-buying-info__title-info">
          ID: {{ props.info.incrementing_id }}
        </div>
        <UiSeparator type="dot" />
        <div class="media-buying-info__title-name">
          {{ titleText }}
        </div>
        <UiIcon
          v-if="props.info.needs_attention"
          :size="20"
          color="var(--text-negative)"
          name="exclamation-circle"
        />
        <UiSeparator type="dot" />
        <div class="media-buying-info__title-info">
          {{ $filters.formatDate3(props.info.created_at!) }}
        </div>
      </div>
      <div class="t:flex t:items-center t:gap-8">
        <slot name="buttons" />
      </div>
      <UiSeparator />
      <div class="t:flex t:gap-32">
        <div class="media-buying-info__block media-buying-info__block_side t:space-y-24">
          <div class="media-buying-info__affiliate">
            <CardTitle :title="$t('mediaBuying.actions.profile')" />
            <div class="t:px-8">
              <CardItem
                no-value
                no-border
                is-label-ellipsis
                :item="{
                  name: info.affiliate_email,
                  labelClass: 'media-buying-info__affiliate-name',
                  labelLink: `/affiliates/${info.affiliate_id}/info`
                }"
              />
              <CardItem
                v-if="info.team_name"
                :item="{
                  name: $t('mediaBuying.other.team'),
                  value: info.team_name,
                }"
              />
              <CardItem>
                <template v-slot:title>
                  {{ $t('affiliates.cardView.info.balance') }}
                </template>
                <template v-slot:value>
                  <div
                    class="media-buying-info__item-value media-buying-info__item-value_positive t:cursor-pointer"
                    @click="handleAffiliateBalance"
                  >
                    {{ formatCurrencyValue(currencyCode, balanceFieldsAffiliateConverted.balance?.approved) }}
                    <UiChevron :rotate="!expandedAffiliateBalance" />
                  </div>
                </template>
              </CardItem>
              <template v-if="expandedAffiliateBalance">
                <CardItem
                  v-for="(balance, key) in info.affiliate?.money.balance.approved"
                  :key="key"
                >
                  <template v-slot:title>
                    <div />
                  </template>
                  <template v-slot:value>
                    <div class="media-buying-info__item-value media-buying-info__item-value_positive">
                      {{ formatCurrencyValue(key, balance) }}
                    </div>
                  </template>
                </CardItem>
              </template>
              <CardItem
                v-for="item in affiliateInfo"
                :key="item.name"
                :item="item"
              />
            </div>
          </div>
          <div class="media-buying-info__company">
            <CardTitle
              :title="$t('mediaBuying.other.camp_info')"
            />
            <div class="t:px-8">
              <CardItem
                no-value
                no-border
              >
                <template v-slot:title>
                  <div class="t:space-y-8">
                    <div
                      v-if="!isRootCampaign"
                      class="media-buying-info__company-info"
                    >
                      {{ info.root_campaign_name }}: ID {{ info.root_campaign_incrementing_id }}
                    </div>
                    <div
                      :class="[
                        't:space-y-4',
                        't:break-all',
                        {'media-buying-info__tree': !isRootCampaign}
                      ]"
                    >
                      <div class="media-buying-info__company-info">
                        ID {{ info.campaign_incrementing_id }}
                      </div>
                      <RouterLink
                        v-if="hasLinkToReports"
                        class="media-buying-info__item-title-link"
                        :to="{
                          path: '/media-buying/reports',
                          query: {
                            group_by: 'campaign_id',
                            sort_by: 'campaign_name',
                            affiliate_id: props.info.affiliate_id,
                            affiliate_email: props.info.affiliate_email,
                            root_campaign_name: `(ID: ${ props.info.root_campaign_incrementing_id }) ${props.info.root_campaign_name}`,
                            root_campaign_id: props.info.root_campaign_id,
                          }
                        }"
                        target="_blank"
                      >
                        {{ props.info.campaign_name }}
                        <UiIcon
                          name="angle-right"
                          :size="16"
                          color="inherit"
                        />
                      </RouterLink>
                      <UiLabel
                        v-else
                        :label="props.info.campaign_name"
                        :font-size="16"
                        bold
                      />
                    </div>
                    <div class="t:break-all">
                      {{ props.info?.campaign?.description }}
                    </div>
                  </div>
                </template>
              </CardItem>
              <CardItem>
                <template v-slot:title>
                  {{ $t('affiliates.cardView.info.balance') }}
                </template>
                <template v-slot:value>
                  <div
                    class="media-buying-info__item-value media-buying-info__item-value_positive t:cursor-pointer"
                    @click="handleCampaignBalance"
                  >
                    {{ formatCurrencyValue(props.currencyCode, balanceFieldsCampaignConverted.balance?.approved) }}
                    <UiChevron
                      :rotate="!expandedCampaignBalance"
                    />
                  </div>
                </template>
              </CardItem>
              <template
                v-if="expandedCampaignBalance"
              >
                <CardItem
                  v-for="(balance, key) in props.info.campaign?.money.balance.approved"
                  :key="key"
                >
                  <template v-slot:title>
                    <div />
                  </template>
                  <template v-slot:value>
                    <div class="media-buying-info__item-value media-buying-info__item-value_positive">
                      {{ formatCurrencyValue(key, balance) }}
                    </div>
                  </template>
                </CardItem>
              </template>
              <CardItem
                v-for="item in campaignInfo"
                :key="item.name"
                :item="item"
              />
            </div>
          </div>
        </div>
        <div class="media-buying-info__block t:space-y-24">
          <CardBlock
            :title="$t('mediaBuying.info.payment_info_card.title')"
          >
            <template v-slot:actionOuter>
              <div class="t:flex t:gap-8">
                <template v-if="props.type === 'payment'">
                  <UiStatusBlock
                    :title="$t(`mediaBuying.status.moderation`)"
                    :status="props.info.moderation_status"
                    :status-text="$t(`mediaBuying.status.${props.info.moderation_status}`)"
                  />
                  <UiStatusBlock
                    :title="$t(`mediaBuying.status.payout`)"
                    :status="props.info.payout_status"
                    :status-text="$t(`mediaBuying.status.${props.info.payout_status}`)"
                  />
                </template>
                <UiStatusBlock
                  v-else
                  :title="$t(`mediaBuying.status.moderation`)"
                  :status="props.info.status"
                  :status-text="$t(`mediaBuying.status.${props.info.status}`)"
                />
              </div>
            </template>
            <template v-slot:cardItem>
              <CardItem no-border>
                <template v-slot:value>
                  <div class="media-buying-info__request">
                    <div class="t:space-y-4">
                      <div class="t:flex t:items-center t:gap-8">
                        <template v-if="props.info.review_requested_at">
                          <div
                            class="media-buying-info__request-date"
                            v-html="$t(`mediaBuying.info.payment_info_card.requested_at`, {date: $filters.formatDate3(props.info.review_requested_at)})"
                          />
                          <UiSeparator
                            type="dot"
                            color="--background-primary"
                          />
                        </template>
                        <div class="t:flex t:items-center">
                          <UiIcon
                            class="t:mr-2"
                            color="var(--text-body-secondary)"
                            :size="12"
                            name="history"
                          />
                          <div
                            class="media-buying-info__request-date"
                            v-html="$t(`mediaBuying.info.payment_info_card.updated_at`, {date: $filters.formatDate3(props.info.updated_at || '')})"
                          />
                        </div>
                      </div>
                      <div class="t:flex t:items-center t:gap-8">
                        <div class="media-buying-info__request-amount">
                          {{ formatCurrencyValue(props.info.currency_code, props.info.amount) }}
                        </div>
                        <template
                          v-if="props.info.currency_code !== currencyCode"
                        >
                          <UiSeparator type="vertical" />
                          <div class="media-buying-info__request-amount-convert">
                            {{ formatCurrencyValue(currencyCode, info.amount_usd) }}
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </CardItem>
              <template v-if="props.type === 'payment'">
                <CardItem>
                  <template v-slot:title>
                    {{ $t('mediaBuying.info.payment_info_card.method') }}
                  </template>
                  <template v-slot:value>
                    <img
                      v-if="methodImgUrl"
                      :src="methodImgUrl"
                      class="media-buying-info__method-img"
                      alt=""
                    >
                    <div v-else>
                      {{ props.info.payment_method_name }}
                    </div>
                  </template>
                </CardItem>
                <CardItem>
                  <template v-slot:title>
                    {{ $t('mediaBuying.info.payment_info_card.wallet') }}
                  </template>
                  <template
                    v-if="props.info.payment_wallet"
                    v-slot:value
                  >
                    <div
                      class="media-buying-info__item-value media-buying-info__item-value_positive t:cursor-pointer"
                      @click="copy(props.info.payment_wallet)"
                    >
                      {{ props.info.payment_wallet }}
                      <UiIcon
                        color="inherit"
                        name="clone"
                      />
                    </div>
                  </template>
                </CardItem>
                <CardItem>
                  <template v-slot:title>
                    {{ $t('mediaBuying.info.payment_info_card.period') }}
                  </template>
                  <template v-slot:value>
                    {{ $filters.formatDate3(('payment_period_start' in props.info ? props.info.payment_period_start : '') || '') }} -
                    {{ $filters.formatDate3(('payment_period_end' in props.info ? props.info.payment_period_end : '') || '') }}
                  </template>
                </CardItem>
              </template>
              <template v-if="props.type === 'post'">
                <CardItem>
                  <template v-slot:title>
                    {{ $t('mediaBuying.info.payment_info_card.type') }}
                  </template>
                  <template
                    v-if="props.info.type"
                    v-slot:value
                  >
                    {{ $t(`mediaBuying.types.${props.info.type}`) }}
                  </template>
                </CardItem>
                <CardItem>
                  <template v-slot:title>
                    {{ $t('mediaBuying.info.post_info_card.period') }}
                  </template>
                  <template v-slot:value>
                    {{ $filters.formatDate3(('post_period_start' in props.info ? props.info.post_period_start : '') || '') }} -
                    {{ $filters.formatDate3(('post_period_end' in props.info ? props.info.post_period_end : '') || '') }}
                  </template>
                </CardItem>
              </template>
              <CardItem>
                <template v-slot:value>
                  <div class="media-buying-info__description">
                    <div class="media-buying-info__description-title">
                      {{ props.type === 'payment'
                        ? $t(`mediaBuying.info.payment_description_card.title`)
                        : $t(`mediaBuying.info.post_info_card.description`) }}
                    </div>
                    {{ info.description }}
                  </div>
                </template>
              </CardItem>
            </template>
          </CardBlock>
          <CardBlock
            v-if="urlFilter.length"
            :title="$t(`mediaBuying.info.post_info_card.urls`)"
          >
            <template v-slot:cardItem>
              <CardItem
                v-for="(url, key) in urlFilter"
                :key="key"
                :no-border="key === 0"
              >
                <template v-slot:value>
                  <div class="t:space-y-8 t:truncate">
                    <div class="t:flex t:items-center t:gap-8">
                      <a
                        :href="url.url"
                        class="media-buying-info__link t:truncate"
                        target="_blank"
                      >
                        <img
                          :src="getIcon(url)"
                          alt=""
                        >
                        <span class="t:truncate">{{ url.url }}</span>
                      </a>
                      <UiIcon
                        class="t:shrink-0 t:cursor-pointer"
                        color="var(--text-positive)"
                        name="clone"
                        @click="copy(url.url)"
                      />
                    </div>
                    <div
                      v-if="props.type === 'payment'
                        && typeGuard<GetMediaBuyingPaymentsByIdResponse['payload']['urls'][number]>(url)
                        && url.duplicates?.length"
                      class="t:flex t:items-center t:space-x-8"
                    >
                      <PaymentDuplicates
                        :duplicates="url.duplicates"
                        :has-permission="hasPermission('view_profile')"
                      />
                      <RouterLink
                        :to="`/affiliates/${url.duplicates[0]!.affiliate_id}/info`"
                        class="media-buying-info__link"
                      >
                        {{ url.duplicates[0]!.affiliate_email }}
                      </RouterLink>
                      <UiSeparator type="dot" />
                      <div class="media-buying-info__company-info">
                        {{ url.duplicates[0]!.campaign_name }}: ID {{ url.duplicates[0]!.campaign_incrementing_id }}
                      </div>
                    </div>
                  </div>
                </template>
              </CardItem>
            </template>
          </CardBlock>
          <div class="t:flex t:gap-24">
            <CardBlock
              :title="$t('mediaBuying.other.aff_stats')"
              :list="affiliateStats"
            />
            <CardBlock
              :title="$t('mediaBuying.other.camp_stats')"
              :list="campaignStats"
            >
              <template v-slot:actionOuter>
                <CampaignStatisticFilterPopup
                  :props-period="period"
                  @apply-filters="applyCampaignStatistics"
                />
              </template>
            </CardBlock>
          </div>
          <CardBlock
            v-if="props.type === 'payment' && props.info.additional_payout_information"
            :title="$t('mediaBuying.info.payment_info_card.add_information')"
          >
            <template v-slot:cardItem>
              <CardItem no-border>
                <template v-slot:value>
                  <div v-html="props.info.additional_payout_information" />
                </template>
              </CardItem>
            </template>
          </CardBlock>
          <Comments
            :is-editable="hasPermission('post_comments')"
            :get-method="props.getComments"
            :post-method="props.postComment"
          />
        </div>
        <div class="media-buying-info__block media-buying-info__block_side">
          <FilesList
            :files="props.info.files"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.media-buying-info {
  $root: &;

  &__affiliate-name {
    font-size: 16px;
    font-weight: 500;
  }

  &__title-info {
    color: var(--text-body-secondary);
    font-size: 20px;
    font-weight: 500;
  }
  &__title-name {
    color: var(--text-body);
    font-size: 24px;
    font-weight: 500;
  }

  &__block {
    // 260+260+32+32
    width: calc(100% - 584px);
    &_side {
      flex-shrink: 0;
      width: 260px;
    }
  }

  &__item-title-link {
    color: var(--text-link);
    font-size: 16px;
    font-weight: 500;
    line-height: 140%;

    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__item-value {
    color: var(--text-body);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    display: flex;
    align-items: center;
    gap: 4px;
    &_positive {
      color: var(--text-positive);
    }
    &_negative {
      color: var(--text-negative);
    }
  }
  &__company-info {
    color: var(--text-body-secondary);
    font-size: 12px;
    line-height: 140%;
  }

  &__tree {
    position: relative;
    padding-left: 8px;

    &:before,
    &:after {
      position: absolute;
      left: 0;
      background-color: var(--border-default);
      content: '';
    }

    &:before {
      top: 7px;
      width: 6px;
      height: 1px;

    }

    &:after {
      top: 0;
      width: 1px;
      height: 7px;
    }
  }

  &__method-img {
    max-width: 100px;
    max-height: 28px;
    object-fit: contain;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-link);
    img {
      width: 16px;
      user-select: none;
    }
  }

  &__request-date {
    color: var(--text-body-secondary);
    font-size: 12px;
    span {
      color: var(--text-body);
    }
  }
  &__description-title {
    color: var(--text-body-secondary);
    font-size: 12px;
  }

  &__request-amount {
    color: var(--text-positive);
    font-size: 24px;
    font-weight: 500;
    line-height: 140%;
  }
  &__request-amount-convert {
    color: var(--text-body-secondary);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }
}
</style>
